<template>
  <DropdownStandard
    :hasSearch="true"
    :options="realEstateOptions"
    :searchType="t('general__title__realestate').toLowerCase()"
    :title="title"
    @select="select"
  />
</template>
<script lang="ts" setup>
// Imports
import { toRefs, computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { REAL_ESTATE } from '@/interfaces/realEstate.interface'
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import { BUILDING_PROPERTIES } from '@/interfaces/building.interface'
import { COMPLEX_PROPERTIES } from '@/interfaces/complex.interface'

// Props (defineProps)
interface REAL_ESTATE_FILTER_PROPS {
  realEstates: REAL_ESTATE[]
  selectedBuilding?: BUILDING_PROPERTIES
  selectedComplex?: COMPLEX_PROPERTIES
}
const props = defineProps<REAL_ESTATE_FILTER_PROPS>()
const { realEstates, selectedBuilding, selectedComplex } = toRefs(props)

// Emits (defineEmits)
const emits = defineEmits<{ (e: 'toggleMenu'); (e: 'realEstate', value: REAL_ESTATE): void }>()

// Computed
const realEstateOptions = computed<optionType[]>(() => {
  const options: optionType[] = []
  realEstates.value.forEach((realEstate) => {
    if (realEstate.properties.name === 'Saknas') return
    const { id, displayName, location } = realEstate.properties
    options.push({
      disabled: !location,
      id: id,
      name: displayName,
      selected: false,
      subtitle: '',
      value: []
    })
  })
  options.sort((a, b) => {
    if (a.name < b.name) return -1
    if (a.name > b.name) return 1
    return 0
  })
  return options
})

// Reactive variables (incl. composables)
const { t } = useI18n()
const defaultTitle = realEstateOptions.value.length
  ? t('general__text__select') + ' ' + t('general__title__realestate').toLowerCase()
  : t('general__data__data-missing')
const selected = ref<REAL_ESTATE>()
const title = ref<string>(defaultTitle)

// Methods
function select(id: string) {
  const realEstate = realEstates.value.find((realEstate) => realEstate.id === id)
  selected.value = realEstate
  title.value = selected.value?.properties.displayName ? selected.value?.properties.displayName : ''
  if (realEstate) emits('realEstate', realEstate)
}

// Watchers
watch(selectedBuilding, () => {
  if (selectedBuilding?.value?.realEstateId) {
    const relatedRealEstate = realEstateOptions.value.find(
      (realEstate) => realEstate.id === selectedBuilding?.value?.realEstateId
    )
    title.value = relatedRealEstate?.name ?? t('general__data__data-missing')
  }
})
watch(selectedComplex, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    title.value = defaultTitle
  }
})
</script>
